import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ConfirmationDialogService} from '@services/confirmation-dialog.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {ApiConfigurationService} from '@services/administration/machine/trigger/api-configuration.service';
import {ApiConfigurationDetails} from '@models/machine/api-configuration-details';
import {ApiConfiguration} from '@models/machine/api-configuration';
import {Machine} from '@models/machine/machine';
import {ApiConfigurationEditComponent} from '@components/administration/machine/machine-configuration/trigger/api-configuration/modals/api-configuration-edit/api-configuration-edit.component';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {ModalService} from '@services/modal/modal.service';

@Component({
    selector: 'scitis-admin-api-configuration-action',
    templateUrl: './api-configuration-action.component.html',
    styleUrls: ['./api-configuration-action.component.scss'],
    standalone: false
})
export class ApiConfigurationActionComponent implements OnInit {

  @Input()
  machine: Machine = new Machine();

  @Input()
  userRole: Role = new Role(new User());

  @Input()
  endpoint: ApiConfigurationDetails = new ApiConfigurationDetails();

  @Input()
  apiConfigs: ApiConfigurationDetails[] = [];

  @Output()
  endpointUpdateEvent: EventEmitter<ApiConfiguration> = new EventEmitter<ApiConfiguration>();

  @Output()
  apiConfigsUpdateEvent: EventEmitter<ApiConfigurationDetails[]> = new EventEmitter<ApiConfigurationDetails[]>();

  superOrTechAdmin = false;
  deleteButtonIsDisabled = true;
  endpointIsActive = false;
  private deleteEndpointMessageTitle = '';
  private deleteActiveEndpointMessageBody = '';
  private deleteEndpointMessageBody = '';
  private deleteEndpointMessageOK = '';
  private activateEndpointMessageTitle = '';
  private activateEndpointMessageBody = '';
  private activateEndpointMessageOK = '';
  private deactivateEndpointMessageTitle = '';
  private deactivateEndpointMessageBody = '';
  private deactivateEndpointMessageOK = '';
  private COMPONENT_NAME: string = 'ApiConfigurationActionComponent';

  constructor(
      private translate: TranslateService,
      private confirmationDialogService: ConfirmationDialogService,
      private loadingIndicator: LoadingIndicatorService,
      private notification: NotificationService,
      private apiConfigurationService: ApiConfigurationService,
      private modalService: ModalService) {
    this.deleteEndpointMessageTitle = this.translate.instant('Delete API endpoint');
    this.deleteActiveEndpointMessageBody = this.translate.instant('This is an active endpoint.');
    this.deleteEndpointMessageBody = this.translate.instant('Do you really want to permanently delete the API endpoint %s ?');
    this.deleteEndpointMessageOK = this.translate.instant('Delete');

    this.activateEndpointMessageTitle = this.translate.instant('Activate endpoint');
    this.activateEndpointMessageBody = this.translate.instant('Do you really want to activate the API endpoint %s?');
    this.activateEndpointMessageOK = this.translate.instant('Activate');

    this.deactivateEndpointMessageTitle = this.translate.instant('Deactivate API endpoint');
    this.deactivateEndpointMessageBody = this.translate.instant('Do you really want to deactivate the API endpoint %s?');
    this.deactivateEndpointMessageOK = this.translate.instant('Deactivate');

  }

  ngOnInit() {
    this.superOrTechAdmin = this.userRole.isSuperAdminRole || this.userRole.isTechAdminRole;
    this.endpointIsActive = this.endpoint.isActive === true;
    this.deleteButtonIsDisabled = this.endpointIsActive && !this.userRole.isSuperAdminRole && !this.userRole.isTechAdminRole;
  }

  onRemoveApiConfig(): void {
    const deleteEndpointMessageBody = this.getDeleteEndpointMessageBody();
    this.confirmationDialogService.handleConfirmationDialog(this.deleteEndpointMessageTitle, deleteEndpointMessageBody, '',
        this.deleteEndpointMessageOK).subscribe(userConfirmed => {
      if (userConfirmed && this.machine.id !== undefined && this.endpoint.id !== undefined) {
        this.loadingIndicator.show(this.translate.instant('Deleting'));
        this.apiConfigurationService.delete(this.machine.id, this.endpoint.id).subscribe((apiConfiguration: ApiConfiguration) => {
          this.endpointUpdateEvent.emit(apiConfiguration);
          this.loadingIndicator.hide();
          this.notification.success(this.translate.instant('API endpoint deleted'), this.COMPONENT_NAME, false);
        }, (error) => {
          this.loadingIndicator.hide();
          this.notification.error(this.translate.instant('Error deleting API endpoint'), error, this.COMPONENT_NAME, true);
        });
      }
    }, (_error) => {
      this.loadingIndicator.hide();
    });
  }

  onEditApiEndpoint(): void {
    const modalRef = this.modalService.open(ApiConfigurationEditComponent, {size: 'lg'});
    modalRef.componentInstance.machine = this.machine;
    modalRef.componentInstance.apiConfigs = this.apiConfigs;
    modalRef.componentInstance.endpoint = this.endpoint;
    modalRef.componentInstance.userRole = this.userRole;

    modalRef.result.then((result: ApiConfiguration) => {
      this.apiConfigs = [...result.apiConfigs];
      this.apiConfigsUpdateEvent.emit(result.apiConfigs);
    });
  }

  onActivateEndpoint(): void {
    const activateEndpointMessageBody = this.activateEndpointMessageBody.replace('%s', this.endpoint.url.toBold());
    this.confirmationDialogService.handleConfirmationDialog(this.activateEndpointMessageTitle, activateEndpointMessageBody, '',
        this.activateEndpointMessageOK).subscribe(userConfirmed => {
      if (userConfirmed && this.machine.id !== undefined && this.endpoint.id !== undefined) {
        this.loadingIndicator.show(this.translate.instant('Activating endpoint'));
        this.apiConfigurationService.activateEndpoint(this.machine.id, this.endpoint.id).subscribe((apiConfiguration: ApiConfiguration) => {
          this.endpointUpdateEvent.emit(apiConfiguration);
          this.loadingIndicator.hide();
          this.notification.success(this.translate.instant('API endpoint activated, endpoint:') + ' /' + this.endpoint.url, this.COMPONENT_NAME, false);
        }, (error) => {
          this.loadingIndicator.hide();
          this.notification.error(this.translate.instant('Error activating API configuration'), error, this.COMPONENT_NAME, true);
        });
      }
    }, (_error) => {
      this.loadingIndicator.hide();
    });
  }

  onDeactivateEndpoint(): void {
    const deactivateEndpointMessageBody = this.deactivateEndpointMessageBody.replace('%s', this.endpoint.url.toBold());
    this.confirmationDialogService.handleConfirmationDialog(this.deactivateEndpointMessageTitle, deactivateEndpointMessageBody, '',
        this.deactivateEndpointMessageOK).subscribe(userConfirmed => {
      if (userConfirmed && this.machine.id !== undefined && this.endpoint.id !== undefined) {
        this.loadingIndicator.show(this.translate.instant('Deactivating endpoint'));
        this.apiConfigurationService.deactivateEndpoint(this.machine.id, this.endpoint.id).subscribe((apiConfiguration: ApiConfiguration) => {
          this.endpointUpdateEvent.emit(apiConfiguration);
          this.loadingIndicator.hide();
          this.notification.success(this.translate.instant('API endpoint deactivated, endpoint:') + ' /' + this.endpoint.url, this.COMPONENT_NAME, false);
        }, (error) => {
          this.loadingIndicator.hide();
          this.notification.error(this.translate.instant('Error deactivating API configuration'), error, this.COMPONENT_NAME, true);
        });
      }
    }, (_error) => {
      this.loadingIndicator.hide();
    });
  }

  getDeleteEndpointMessageBody(): string {
    let deleteEndpointMessageBody: string;
    if (this.endpoint.isActive) {
      deleteEndpointMessageBody = this.deleteActiveEndpointMessageBody;
      deleteEndpointMessageBody += ' ' + this.deleteEndpointMessageBody.replace('%s', this.endpoint.url.toBold());
    } else {
      deleteEndpointMessageBody = this.deleteEndpointMessageBody.replace('%s', this.endpoint.url.toBold());
    }
    return deleteEndpointMessageBody;
  }

}
